import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  wooGetOrder,
  wooUpdateOrder,
  wooGetRefunds,
  wooCreateRefund,
  wooGetOrderNotes,
  wooCreateOrderNote,
  WooOrder,
  WooOrderStatus,
  WooRefund,
  WooOrderNote,
} from "../../services/woo";
import { showToast } from "../../assets/util/showToast";
import "../../assets/styles/timeline.css";

function formatDate(iso?: string | null) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMoney(value: number, currency = "USD") {
  if (Number.isNaN(value)) value = 0;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(value);
  } catch (e) {
    // Fallback if unknown currency
    return `$${value.toFixed(2)}`;
  }
}

function mapWooStatus(
  status: WooOrderStatus
): "paid" | "pending" | "refunded" | "cancelled" {
  switch (status) {
    case "completed":
    case "processing":
      return "paid";
    case "refunded":
      return "refunded";
    case "cancelled":
    case "failed":
    case "trash":
      return "cancelled";
    case "pending":
    case "on-hold":
    default:
      return "pending";
  }
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    paid: "success",
    pending: "warning",
    refunded: "error",
    cancelled: "secondary",
  };
  return (
    <span className={`badge bg-${map[status] || "secondary"} text-capitalize`}>
      {status}
    </span>
  );
}

export default function OrderView() {
  const { id } = useParams<{ id: string }>();

  const [order, setOrder] = useState<WooOrder | null>(null);
  const [refunds, setRefunds] = useState<WooRefund[]>([]);
  const [notes, setNotes] = useState<WooOrderNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const currency = order?.currency || "USD";
  const uiStatus = useMemo(
    () => (order ? mapWooStatus(order.status) : "pending"),
    [order]
  );

  async function reload() {
    if (!id) return;
    try {
      setLoading(true);
      const [o] = await Promise.all([wooGetOrder(id)]);
      setOrder(o);
      setError(null);
      // Load refunds & notes in parallel but non-blocking for first paint
      try {
        const [r, n] = await Promise.all([
          wooGetRefunds(o.id),
          wooGetOrderNotes(o.id),
        ]);
        setRefunds(r);
        setNotes(n);
      } catch (e) {
        // Do not block page if secondary data fails
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load order");
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function markAsPaid() {
    if (!order) return;
    try {
      setSaving(true);
      const updated = await wooUpdateOrder(order.id, { status: "processing" });
      setOrder(updated);
      showToast("Order marked as paid (processing)", "success");
    } catch (e: any) {
      showToast(e?.message || "Failed to mark as paid", "error");
    } finally {
      setSaving(false);
    }
  }

  async function markAsShipped() {
    if (!order) return;
    try {
      setSaving(true);
      const updated = await wooUpdateOrder(order.id, { status: "completed" });
      setOrder(updated);
      showToast("Order marked as completed (shipped)", "success");
    } catch (e: any) {
      showToast(e?.message || "Failed to mark as shipped", "error");
    } finally {
      setSaving(false);
    }
  }

  async function cancelOrder() {
    if (!order) return;
    if (!window.confirm(`Cancel order ${order.number || order.id}?`)) return;
    try {
      setSaving(true);
      const updated = await wooUpdateOrder(order.id, { status: "cancelled" });
      setOrder(updated);
      showToast("Order cancelled", "info");
    } catch (e: any) {
      showToast(e?.message || "Failed to cancel order", "error");
    } finally {
      setSaving(false);
    }
  }

  async function processRefund() {
    if (!order) return;
    const max = parseFloat(order.total || "0");
    const rawAmount = window.prompt(
      `Enter refund amount (max ${formatMoney(max, currency)}):`,
      max.toFixed(2)
    );
    if (!rawAmount) return;
    const amount = parseFloat(rawAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      showToast("Invalid amount", "info");
      return;
    }
    const reason = window.prompt("Reason for refund? (optional)", "");

    try {
      setSaving(true);
      await wooCreateRefund(order.id, {
        amount: amount.toFixed(2),
        reason: reason || undefined,
        refund_payment: true,
        restock_items: true,
      });
      showToast("Refund created", "success");
      await reload();
    } catch (e: any) {
      showToast(e?.message || "Failed to create refund", "error");
    } finally {
      setSaving(false);
    }
  }

  async function addNote(customerVisible: boolean) {
    if (!order) return;
    const note = window.prompt(
      customerVisible ? "Message to customer:" : "Private note:",
      ""
    );
    if (!note) return;
    try {
      await wooCreateOrderNote(order.id, {
        note,
        customer_note: customerVisible,
      });
      const n = await wooGetOrderNotes(order.id);
      setNotes(n);
    } catch (e: any) {
      showToast(e?.message || "Failed to add note", "error");
    }
  }

  const computed = useMemo(() => {
    if (!order) return null;
    const items = order.line_items || [];
    const subtotal = items.reduce(
      (sum, li) => sum + parseFloat(li.subtotal || "0"),
      0
    );
    const discount = parseFloat(order.discount_total || "0");
    const shipping = parseFloat(order.shipping_total || "0");
    const tax = parseFloat(order.total_tax || "0");
    const total = parseFloat(order.total || "0");
    return { items, subtotal, discount, shipping, tax, total };
  }, [order]);

  if (loading) {
    return (
      <div className="page-layout">
        <div className="page-header">
          <h2 className="title">Order</h2>
        </div>
        <div className="section-card">
          <div className="card">
            <div className="card-body">Loading…</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order || !computed) {
    return (
      <div className="page-layout">
        <div className="page-header">
          <div className="d-flex align-items-center">
            <Link
              to="/account/orders"
              className="btn btn-outline-secondary me-3"
            >
              <i className="fa-solid fa-arrow-left me-2"></i>
              Back to Orders
            </Link>
            <h2 className="title mb-0">Order Not Found</h2>
          </div>
        </div>

        <div className="section-card">
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="fa-solid fa-exclamation-triangle fa-3x text-warning mb-3"></i>
              <h4>Order Not Found</h4>
              <p className="text-muted">
                {error ||
                  "The order you're looking for doesn't exist or may have been deleted."}
              </p>
              <Link to="/account/orders" className="btn btn-primary">
                Return to Orders
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { items, subtotal, discount, shipping, tax, total } = computed;

  return (
    <div className="page-layout">
      <div className="page-header">
        <div>
          <div className="d-flex align-items-center gap-3">
            <h2 className="title mb-0">Order #{order.number || order.id}</h2>
            <StatusBadge status={uiStatus} />
          </div>
          <p className="text-muted mb-0">
            Placed on {formatDate(order.date_created)}
          </p>
        </div>
        <div className="right">
          <Link to="/account/orders" className="btn btn-outline-secondary me-3">
            <i className="fa-solid fa-arrow-left me-2"></i>
            Back to Orders
          </Link>
        </div>
      </div>

      <div className="section-card">
        <div className="row g-3">
          {/* Order Items */}
          <div className="col-lg-8">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-3">
                  <i className="fa-solid fa-shopping-bag me-2"></i>
                  Order Items
                </h5>

                <div className="table-responsive">
                  <table className="table align-middle">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th className="text-center">Quantity</th>
                        <th className="text-end">Unit Price</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((li) => (
                        <tr key={li.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              {li.image?.src && (
                                <img
                                  src={li.image.src}
                                  alt={li.name || "Item"}
                                  className="me-3 rounded"
                                  style={{
                                    width: 60,
                                    height: 60,
                                    objectFit: "cover",
                                  }}
                                />
                              )}
                              <div>
                                <h6 className="mb-1">{li.name}</h6>
                                {li.sku && (
                                  <small className="text-muted">
                                    SKU: {li.sku}
                                  </small>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-light text-dark">
                              {li.quantity}
                            </span>
                          </td>
                          <td className="text-end">
                            {formatMoney(
                              li.price || parseFloat(li.total || "0"),
                              currency,
                            )}
                          </td>
                          <td className="text-end">
                            <strong>
                              {formatMoney(
                                parseFloat(li.total || "0"),
                                currency,
                              )}
                            </strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Order Timeline */}
            <div className="card mt-3">
              <div className="card-body">
                <h5 className="card-title mb-3">
                  <i className="fa-solid fa-clock-rotate-left me-2"></i>
                  Order Timeline
                </h5>

                <div className="timeline">
                  <div className="timeline-item">
                    <div className="timeline-marker bg-success">
                      <i className="fa-solid fa-shopping-cart text-white"></i>
                    </div>
                    <div className="timeline-content">
                      <h6 className="mb-1">Order Placed</h6>
                      <p className="text-muted mb-0">
                        {formatDate(order.date_created)}
                      </p>
                    </div>
                  </div>

                  {uiStatus === "paid" && (
                    <div className="timeline-item">
                      <div className="timeline-marker bg-primary">
                        <i className="fa-solid fa-credit-card text-white"></i>
                      </div>
                      <div className="timeline-content">
                        <h6 className="mb-1">Payment Confirmed</h6>
                        <p className="text-muted mb-0">
                          {formatDate(order.date_paid || order.date_modified)}
                        </p>
                      </div>
                    </div>
                  )}

                  {order.status === "processing" && (
                    <div className="timeline-item">
                      <div className="timeline-marker bg-info">
                        <i className="fa-solid fa-box text-white"></i>
                      </div>
                      <div className="timeline-content">
                        <h6 className="mb-1">Processing</h6>
                        <p className="text-muted mb-0">
                          Order is being prepared for shipment
                        </p>
                      </div>
                    </div>
                  )}

                  {order.status === "completed" && (
                    <div className="timeline-item">
                      <div className="timeline-marker bg-info">
                        <i className="fa-solid fa-truck text-white"></i>
                      </div>
                      <div className="timeline-content">
                        <h6 className="mb-1">Shipped / Completed</h6>
                        <p className="text-muted mb-0">
                          {formatDate(order.date_completed)}
                        </p>
                      </div>
                    </div>
                  )}

                  {refunds.length > 0 && (
                    <div className="timeline-item">
                      <div className="timeline-marker bg-danger">
                        <i className="fa-solid fa-undo text-white"></i>
                      </div>
                      <div className="timeline-content">
                        <h6 className="mb-1">Refunds</h6>
                        {refunds.map((r) => (
                          <p className="text-muted mb-0" key={r.id}>
                            {formatDate(r.date_created)} —{" "}
                            {formatMoney(parseFloat(r.amount || "0"), currency)}{" "}
                            {r.reason ? `— ${r.reason}` : ""}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="card mt-3">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">
                    <i className="fa-solid fa-comment-dots me-2"></i>
                    Notes
                  </h5>
                  <div className="btn-group">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => addNote(false)}
                    >
                      Add Private
                    </button>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => addNote(true)}
                    >
                      Add Customer Note
                    </button>
                  </div>
                </div>

                {notes.length === 0 && (
                  <div className="text-muted">No notes yet.</div>
                )}

                {notes.length > 0 && (
                  <ul className="list-unstyled mb-0">
                    {notes.map((n) => (
                      <li key={n.id} className="mb-2">
                        <div className="small text-muted">
                          {formatDate(n.date_created)}
                          {n.customer_note ? " — Customer" : " — Private"}
                        </div>
                        <div>{n.note}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Order Summary & Customer Info */}
          <div className="col-lg-4">
            {/* Order Summary */}
            <div className="card ">
              <div className="card-body">
                <h5 className="card-title mb-3">
                  <i className="fa-solid fa-receipt me-2"></i>
                  Order Summary
                </h5>

                <div className="d-flex justify-content-between mb-2">
                  <span>Subtotal:</span>
                  <span>{formatMoney(subtotal, currency)}</span>
                </div>
                {discount > 0 && (
                  <div className="d-flex justify-content-between mb-2">
                    <span>Discount:</span>
                    <span>-{formatMoney(discount, currency)}</span>
                  </div>
                )}
                <div className="d-flex justify-content-between mb-2">
                  <span>Tax:</span>
                  <span>{formatMoney(tax, currency)}</span>
                </div>
                <div className="d-flex justify-content-between mb-3">
                  <span>Shipping:</span>
                  <span>
                    {shipping === 0 ? "FREE" : formatMoney(shipping, currency)}
                  </span>
                </div>
                <hr />
                <div className="d-flex justify-content-between">
                  <strong>Total:</strong>
                  <strong>{formatMoney(total, currency)}</strong>
                </div>

                <div className="mt-3 pt-3 border-top">
                  <small className="text-muted">
                    <strong>Payment Status:</strong>{" "}
                    <StatusBadge status={uiStatus} />
                  </small>
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="card mt-3">
              <div className="card-body">
                <h5 className="card-title mb-3">
                  <i className="fa-solid fa-user me-2"></i>
                  Customer Information
                </h5>

                <div className="mb-3">
                  <h6 className="mb-1">Customer</h6>
                  <p className="mb-0">
                    {order.billing?.first_name} {order.billing?.last_name}
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="mb-1">Billing Address</h6>
                  <p className="mb-0">
                    {order.billing?.address_1}
                    <br />
                    {order.billing?.address_2 && (
                      <>
                        {order.billing?.address_2}
                        <br />
                      </>
                    )}
                    {order.billing?.city}, {order.billing?.state}{" "}
                    {order.billing?.postcode}
                    <br />
                    {order.billing?.country}
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="mb-1">Shipping Address</h6>
                  <p className="mb-0">
                    {order.shipping?.address_1}
                    <br />
                    {order.shipping?.address_2 && (
                      <>
                        {order.shipping?.address_2}
                        <br />
                      </>
                    )}
                    {order.shipping?.city}, {order.shipping?.state}{" "}
                    {order.shipping?.postcode}
                    <br />
                    {order.shipping?.country}
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="mb-1">Contact</h6>
                  <p className="mb-0">
                    <i className="fa-solid fa-envelope me-2 text-muted"></i>
                    {order.billing?.email}
                    <br />
                    <i className="fa-solid fa-phone me-2 text-muted"></i>
                    {order.billing?.phone}
                  </p>
                </div>
              </div>
            </div>

            {/* Order Actions */}
            <div className="card mt-3">
              <div className="card-body">
                <h5 className="card-title mb-3">
                  <i className="fa-solid fa-cog me-2"></i>
                  Actions
                </h5>

                <div className="d-grid gap-2">
                  {uiStatus === "pending" && (
                    <>
                      <button
                        className="btn btn-success"
                        disabled={saving}
                        onClick={markAsPaid}
                      >
                        <i className="fa-solid fa-check me-2"></i>
                        Mark as Paid
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        disabled={saving}
                        onClick={cancelOrder}
                      >
                        <i className="fa-solid fa-times me-2"></i>
                        Cancel Order
                      </button>
                    </>
                  )}

                  {uiStatus === "paid" && (
                    <>
                      <button
                        className="btn btn-primary"
                        disabled={saving}
                        onClick={markAsShipped}
                      >
                        <i className="fa-solid fa-truck me-2"></i>
                        Mark as Shipped
                      </button>
                      {/* <button
                        className="btn btn-outline-warning"
                        disabled={saving}
                        onClick={processRefund}
                      >
                        <i className="fa-solid fa-undo me-2"></i>
                        Process Refund
                      </button> */}
                    </>
                  )}

                  {/* <button className="btn btn-outline-primary" disabled>
                    <i className="fa-solid fa-print me-2"></i>
                    Print Invoice
                  </button>

                  <button className="btn btn-outline-secondary" disabled>
                    <i className="fa-solid fa-envelope me-2"></i>
                    Send Email
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
